/**
 * Unified sticky layout renderer.
 * Config: distance, start: { element, viewport }, optional images[].
 * With images: sticky + scroll-based image animations (like former scroll-animated).
 * Without images: sticky only (like former rain layout without rain).
 */

import { areAnimationsEnabled } from '../../../utils/animation-settings'
import { resolveSceneBg, getSceneImagePath, applyResponsiveImage } from '../../../utils/pathResolver'
import type { SceneData } from '../types'

export interface StickyLayoutRendererContext {
  container: HTMLDivElement
  wrapper: HTMLDivElement | null
  imageContainer: HTMLDivElement | null
  data: SceneData
  onImageContainerSet: (container: HTMLDivElement) => void
  onAppendSceneNumberDebug: () => void
  onAfterImageLoad?: () => void
}

type ImageConfig =
  NonNullable<NonNullable<SceneData['layout']>['config']> extends { images?: infer T }
    ? T extends Array<infer U>
      ? U
      : never
    : never

interface StickyDOM {
  container: HTMLDivElement
  imageContainer: HTMLDivElement
  bgImg: HTMLImageElement
  animatedImages: Array<{ img: HTMLImageElement; config: ImageConfig }>
}

function getStickyConfig(data: SceneData) {
  const config = data.layout?.config || {}
  const distance = config.distance ?? 3
  const start = config.start ?? { element: 50, viewport: 50 }
  const viewport = start.viewport ?? 50
  const element = start.element ?? 50
  const images = config.images ?? []
  const unlockAfter = config.unlockAfter ?? 0
  return { distance, start, viewport, element, images, unlockAfter }
}

/**
 * Render sticky layout scene (with or without scroll-animated images).
 */
export function renderStickyLayout(context: StickyLayoutRendererContext): HTMLElement {
  const { data } = context
  const { images } = getStickyConfig(data)
  const hasImages = images.length > 0

  const dom = hasImages ? createStickyDOMWithImages(context) : createStickyDOMSimple(context)
  const runAfterLoad = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setupStickyBehavior({
          wrapper: context.wrapper,
          imageContainer: dom.imageContainer,
          data: context.data,
          ...(hasImages && { animatedImages: (dom as StickyDOM).animatedImages }),
        })
        context.onAppendSceneNumberDebug()
        context.onAfterImageLoad?.()
      })
    })
  }

  if (dom.bgImg.complete) runAfterLoad()
  else dom.bgImg.onload = runAfterLoad

  return dom.container
}

function createStickyDOMSimple(context: StickyLayoutRendererContext): StickyDOM {
  const { container, data, onImageContainerSet } = context
  const imageContainer = document.createElement('div')
  imageContainer.className = 'scene__image scene__image--sticky'
  onImageContainerSet(imageContainer)

  const bgPath = resolveSceneBg(data) ?? ''
  const bgImg = document.createElement('img')
  bgImg.src = bgPath
  bgImg.alt = data.id
  bgImg.className = 'scene__img'
  applyResponsiveImage(bgImg)
  imageContainer.appendChild(bgImg)
  container.appendChild(imageContainer)

  return { container, imageContainer, bgImg, animatedImages: [] }
}

function createStickyDOMWithImages(context: StickyLayoutRendererContext): StickyDOM {
  const { container, data, onImageContainerSet } = context
  const { images: imagesConfig } = getStickyConfig(data)

  const imageContainer = document.createElement('div')
  imageContainer.className = 'scene__image scene__image--sticky scene__image--sticky-animated'
  imageContainer.style.position = 'relative'
  imageContainer.style.width = '100%'
  onImageContainerSet(imageContainer)

  const bgPath = resolveSceneBg(data) ?? ''
  const bgImg = document.createElement('img')
  bgImg.src = bgPath
  bgImg.alt = data.id
  bgImg.className = 'scene__img'
  bgImg.style.width = '100%'
  bgImg.style.height = 'auto'
  bgImg.style.display = 'block'
  applyResponsiveImage(bgImg)
  imageContainer.appendChild(bgImg)

  const animatedImagesContainer = document.createElement('div')
  animatedImagesContainer.className = 'scroll-animated-images-container'
  animatedImagesContainer.setAttribute('data-scene-id', data.id)
  animatedImagesContainer.style.position = 'absolute'
  animatedImagesContainer.style.top = '0'
  animatedImagesContainer.style.left = '0'
  animatedImagesContainer.style.width = '100%'
  animatedImagesContainer.style.height = '100%'
  animatedImagesContainer.style.overflow = 'hidden'

  const animatedImages: StickyDOM['animatedImages'] = []
  imagesConfig.forEach((imageConfig) => {
    const img = document.createElement('img')
    img.src = getSceneImagePath(data.id, (imageConfig as any).image)
    img.alt = (imageConfig as any).image?.replace('.png', '') ?? ''
    img.className = `scroll-animated-image scroll-animated-image--${(imageConfig as any).image?.replace('.png', '') ?? ''}`
    img.style.height = '100%'
    img.style.visibility = 'hidden'
    img.style.opacity = '0'
    animatedImagesContainer.appendChild(img)
    animatedImages.push({ img, config: imageConfig as ImageConfig })
  })

  imageContainer.appendChild(animatedImagesContainer)
  container.appendChild(imageContainer)

  return { container, imageContainer, bgImg, animatedImages }
}

export function setupStickyBehavior(params: {
  wrapper: HTMLDivElement | null
  imageContainer: HTMLDivElement
  data: SceneData
  animatedImages?: StickyDOM['animatedImages']
}): void {
  const { wrapper, imageContainer, data, animatedImages = [] } = params
  if (!wrapper || !imageContainer) return

  if (!areAnimationsEnabled() && animatedImages.length > 0) {
    const vh = window.innerHeight
    animatedImages.forEach(({ img, config }) => {
      const position = (config as any).position || {}
      const toX = position.left ? (window.innerWidth * position.left.to) / 100 : 0
      const toY = position.top ? (vh * position.top.to) / 100 : 0
      const toOpacity = (config as any).opacity ? (config as any).opacity.to : 1
      img.style.transform = `translate3d(${toX}px,${toY}px,0)`
      img.style.opacity = String(toOpacity)
      img.style.visibility = 'visible'
    })
    return
  }

  const { distance, viewport, element, unlockAfter } = getStickyConfig(data)
  const viewportHeight = window.innerHeight
  const stickyDistance = viewportHeight * distance

  const sceneContainer = imageContainer.closest('.scene') as HTMLElement | null
  if (sceneContainer) {
    // sceneContainer.style.minHeight = '100svh'
    sceneContainer.style.justifyContent = 'center'
  }

  const stickyContainer = document.createElement('div')
  stickyContainer.className = 'sticky-layout-container'
  stickyContainer.style.width = '100%'
  stickyContainer.style.position = 'relative'

  const setupSticky = () => {
    const parentElement = wrapper.parentElement
    if (!parentElement) {
      requestAnimationFrame(setupSticky)
      return
    }

    parentElement.insertBefore(stickyContainer, wrapper)
    stickyContainer.appendChild(wrapper)

    const nextSib = stickyContainer.nextElementSibling
    if (!nextSib || !nextSib.classList.contains('scene-spacer')) {
      const spacer = document.createElement('div')
      spacer.className = 'scene-spacer'
      if (stickyContainer.nextSibling) parentElement.insertBefore(spacer, stickyContainer.nextSibling)
      else parentElement.appendChild(spacer)
    }

    wrapper.style.position = 'sticky'
    wrapper.style.zIndex = '1'

    let stickyTopValue = 0

    const updateContainerHeight = () => {
      const wrapperHeight = wrapper.getBoundingClientRect().height || wrapper.offsetHeight
      if (wrapperHeight > 0) {
        const vh = window.innerHeight
        stickyTopValue = Math.max(0, (vh * viewport) / 100 - (wrapperHeight * element) / 100)
        wrapper.style.top = `${stickyTopValue}px`
        stickyContainer.style.height = `${wrapperHeight + stickyDistance}px`
      } else {
        setTimeout(() => {
          const retryHeight = wrapper.getBoundingClientRect().height || wrapper.offsetHeight
          if (retryHeight > 0) {
            const vh = window.innerHeight
            stickyTopValue = Math.max(0, (vh * viewport) / 100 - (retryHeight * element) / 100)
            wrapper.style.top = `${stickyTopValue}px`
            stickyContainer.style.height = `${retryHeight + stickyDistance}px`
          }
        }, 50)
      }
    }

    const images = imageContainer.querySelectorAll('img')
    images.forEach((img) => {
      if (img.complete) updateContainerHeight()
      else img.addEventListener('load', updateContainerHeight, { once: true })
    })
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateContainerHeight()
        setTimeout(updateContainerHeight, 200)
      })
    })

    const getStickyStartScroll = (): number => {
      const containerRect = stickyContainer.getBoundingClientRect()
      return containerRect.top + window.scrollY - stickyTopValue
    }

    const totalImages = animatedImages.length
    const sortedByOrder = [...animatedImages].sort((a, b) => {
      const orderA = (a.config as any).order ?? Infinity
      const orderB = (b.config as any).order ?? Infinity
      return orderA - orderB
    })
    const imageTimings = animatedImages.map(({ img, config }, index) => {
      const timing = (config as any).timing || {}
      let sortedIndex = index
      sortedByOrder.forEach((item, i) => {
        if (item.img === img) sortedIndex = i
      })
      const start = timing.start ?? sortedIndex / totalImages
      const duration = timing.duration ?? 1 / totalImages
      return { start, duration, end: start + duration }
    })

    const updateScrollProgress = () => {
      if (totalImages === 0) return
      const scrollStartY = getStickyStartScroll()
      const relativeScroll = window.scrollY - scrollStartY
      const progress = Math.max(0, Math.min(1, relativeScroll / stickyDistance))
      const vw = window.innerWidth

      for (let i = 0; i < totalImages; i++) {
        const { img, config } = animatedImages[i]
        const t = imageTimings[i]
        const position = (config as any).position || {}
        let x = 0
        let y = 0
        if (position.left) {
          const fromX = (vw * position.left.from) / 100
          const toX = (vw * position.left.to) / 100
          if (progress < t.start) x = fromX
          else if (progress <= t.end) x = fromX + ((progress - t.start) / t.duration) * (toX - fromX)
          else x = toX
        }
        if (position.top) {
          const fromY = (viewportHeight * position.top.from) / 100
          const toY = (viewportHeight * position.top.to) / 100
          if (progress < t.start) y = fromY
          else if (progress <= t.end) y = fromY + ((progress - t.start) / t.duration) * (toY - fromY)
          else y = toY
        }
        const opacityConfig = (config as any).opacity
        let opacity = 0
        if (opacityConfig) {
          if (progress < t.start) opacity = opacityConfig.from
          else if (progress <= t.end)
            opacity = opacityConfig.from + ((progress - t.start) / t.duration) * (opacityConfig.to - opacityConfig.from)
          else opacity = opacityConfig.to
        } else {
          if (progress < t.start) opacity = 0
          else if (progress <= t.end) opacity = (progress - t.start) / t.duration
          else opacity = 1
        }
        img.style.transform = `translate3d(${x}px,${y}px,0)`
        img.style.opacity = String(opacity)
        img.style.visibility = progress >= t.start ? 'visible' : 'hidden'
      }
    }

    let scrollRafId: number | null = null
    const scrollHandler = () => {
      if (scrollRafId === null) {
        scrollRafId = requestAnimationFrame(() => {
          updateScrollProgress()
          scrollRafId = null
        })
      }
    }
    window.addEventListener('scroll', scrollHandler, { passive: true })

    const resizeObserver = new ResizeObserver(() => {
      updateContainerHeight()
      updateScrollProgress()
    })
    resizeObserver.observe(wrapper)
    resizeObserver.observe(stickyContainer)
    requestAnimationFrame(() => updateScrollProgress())
    ;(wrapper as any).__stickyLayoutCleanup = () => {
      if (scrollRafId !== null) cancelAnimationFrame(scrollRafId)
      window.removeEventListener('scroll', scrollHandler)
      resizeObserver.disconnect()
      if (stickyContainer.parentElement && wrapper.parentElement === stickyContainer) {
        stickyContainer.parentElement.insertBefore(wrapper, stickyContainer)
      }
      if (stickyContainer.parentElement) {
        stickyContainer.parentElement.removeChild(stickyContainer)
      }
      wrapper.style.position = ''
      wrapper.style.top = ''
      wrapper.style.zIndex = ''
    }

    if (unlockAfter > 0) {
      let locked = true
      let correcting = false
      let rafPending = false

      const getLockY = () => getStickyStartScroll()

      const clampScrollToLock = () => {
        if (correcting) return
        const lockY = getLockY()
        if (window.scrollY > lockY + 1) {
          correcting = true
          window.scrollTo({ top: lockY, behavior: 'instant' as ScrollBehavior })
          const lenis = (window as any).__lenis
          if (lenis && (lenis.targetScroll > lockY + 1 || lenis.scroll > lockY + 1)) {
            lenis.scrollTo(lockY, { immediate: true })
          }
          requestAnimationFrame(() => {
            correcting = false
          })
        }
      }

      const isAtLockBoundary = () => {
        const lockY = getLockY()
        const lenis = (window as any).__lenis
        const current = lenis?.targetScroll ?? lenis?.scroll ?? window.scrollY
        return current >= lockY - 2
      }

      const blockWheel = (e: WheelEvent) => {
        if (!locked) return
        if (e.deltaY > 0 && isAtLockBoundary()) {
          e.preventDefault()
          e.stopPropagation()
          clampScrollToLock()
        }
      }

      let touchStartY = 0
      const blockTouchStart = (e: TouchEvent) => {
        touchStartY = e.touches[0]?.clientY ?? 0
      }
      const blockTouchMove = (e: TouchEvent) => {
        if (!locked) return
        const dy = touchStartY - (e.touches[0]?.clientY ?? 0)
        if (dy > 0 && isAtLockBoundary()) {
          e.preventDefault()
          e.stopPropagation()
          clampScrollToLock()
        }
      }

      const lockScrollHandler = () => {
        if (!locked || correcting || rafPending) return
        rafPending = true
        requestAnimationFrame(() => {
          rafPending = false
          if (locked && !correcting) clampScrollToLock()
        })
      }

      window.addEventListener('wheel', blockWheel, { passive: false, capture: true })
      window.addEventListener('touchstart', blockTouchStart, { passive: true, capture: true })
      window.addEventListener('touchmove', blockTouchMove, { passive: false, capture: true })
      window.addEventListener('scroll', lockScrollHandler, { passive: true })
      ;(wrapper as any).__stickyScrollUnlock = () => {
        if (!locked) return
        locked = false
        window.removeEventListener('wheel', blockWheel, { capture: true })
        window.removeEventListener('touchstart', blockTouchStart, { capture: true })
        window.removeEventListener('touchmove', blockTouchMove, { capture: true })
        window.removeEventListener('scroll', lockScrollHandler)
      }

      const origCleanup = (wrapper as any).__stickyLayoutCleanup
      ;(wrapper as any).__stickyLayoutCleanup = () => {
        ;(wrapper as any).__stickyScrollUnlock?.()
        origCleanup?.()
      }
    }
  }

  setupSticky()
}
